import { CommonModule } from '@angular/common';
import { Component, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContractService } from '@app/features/contracts/contract-service';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import {
  CommentDialog,
  CommentDialogData,
} from '@app/shared/dialogs/comment-dialog/comment-dialog';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { filter, switchMap, tap } from 'rxjs';

/**
 * Bouton de suppression d'un contrat, réservé au créateur du contrat tant que celui-ci est
 * en brouillon, et fermé dès qu'une validation du responsable d'exploitation a été acceptée.
 * Le directeur RH échappe à ces restrictions : il peut supprimer tout contrat non supprimé,
 * quel que soit son statut.
 * La suppression est logique ou définitive selon l'historique de validation du contrat —
 * l'arbitrage est fait côté API.
 */
@Component({
  selector: 'app-button-delete-contract',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './button-delete-contract.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './button-delete-contract.scss',
})
export class ButtonDeleteContract extends RxjsDirective {
  contract = input.required<ContractListItem>();
  contractDeleted = output<string>();

  readonly dialog = inject(MatDialog);
  readonly contractService = inject(ContractService);
  readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);

  get canDeleteContract(): boolean {
    const contract = this.contract();
    if (contract.deleted) {
      return false;
    }

    // Le directeur RH peut supprimer n'importe quel contrat, quel que soit son statut et
    // sans être le créateur.
    if (this.authenticatedUserService.hasAnyRole(['cdt-directeur-rh'])) {
      return true;
    }

    // Pour tous les autres, la suppression se limite au brouillon.
    if (contract.status !== 'DRAFT') {
      return false;
    }

    // Dès qu'une validation du responsable d'exploitation a été acceptée, la suppression est
    // réservée au directeur RH, traité ci-dessus.
    const aUneValidationRexAcceptee = (contract.validationRequests ?? []).some(
      (request) => request.type === 'REX_VALIDATION' && request.status === 'ACCEPTED',
    );
    if (aUneValidationRexAcceptee) {
      return false;
    }

    const userId = this.authenticatedUserService.userId;
    return !!userId && contract.createdBy === userId;
  }

  deleteContract(): void {
    const dialogRef = this.dialog.open<CommentDialog, CommentDialogData, string | undefined>(
      CommentDialog,
      {
        data: {
          title: 'Motif de suppression',
          required: true,
        },
        minWidth: '700px',
      },
    );

    this.register(
      dialogRef
        .afterClosed()
        .pipe(
          filter((comment: string | undefined): comment is string => comment !== undefined),
          switchMap((comment: string) => this.contractService.delete(this.contract().id, comment)),
          tap(() => this.contractDeleted.emit(this.contract().id)),
        )
        .subscribe(),
    );
  }
}
